import { Trash2, Send } from 'lucide-react';

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const PartnerCart = ({
  cart,
  updateQuantity,
  removeFromCart,
  orderNotes,
  setOrderNotes,
  cartTotalExcl,
  cartTotalIncl,
  cartVat,
  vatRate,
  submitOrder,
}) => {
  return (
    <div className="space-y-6" data-testid="partner-cart">
      {cart.length === 0 ? (
        <div className="text-center py-12 text-gray-400" data-testid="partner-cart-empty">
          Your cart is empty
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {cart.map((item) => {
              const unitExcl = Number(
                item.partner_price_excl ?? item.base_price_excl ?? item.base_price ?? 0
              );
              const unitIncl = Number(
                item.partner_price_incl ??
                  item.base_price_incl ??
                  unitExcl * (1 + (vatRate ?? 0.15))
              );
              const lineExcl = unitExcl * item.quantity;
              const lineIncl = unitIncl * item.quantity;

              return (
                <div
                  key={item.sku}
                  className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700"
                  data-testid={`partner-cart-item-${item.sku}`}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-1">{item.product_name}</h3>
                      <p className="text-gray-400 text-sm">SKU: {item.sku}</p>
                      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-0.5 text-sm max-w-sm">
                        <span className="text-gray-400">Unit (excl VAT):</span>
                        <span className="text-white text-right">R {fmt(unitExcl)}</span>
                        <span className="text-gray-400">Unit (incl VAT):</span>
                        <span className="text-ewss-cyan font-semibold text-right">
                          R {fmt(unitIncl)}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs mt-1">per {item.unit}</p>
                    </div>

                    <div className="flex flex-col items-end gap-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.sku, item.quantity - 1)}
                          className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600"
                          data-testid={`partner-cart-decrement-${item.sku}`}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            updateQuantity(item.sku, parseInt(e.target.value) || 1)
                          }
                          className="w-20 px-2 py-1 bg-ewss-navy border border-gray-600 rounded text-white text-center"
                          min="1"
                          data-testid={`partner-cart-qty-${item.sku}`}
                        />
                        <button
                          onClick={() => updateQuantity(item.sku, item.quantity + 1)}
                          className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600"
                          data-testid={`partner-cart-increment-${item.sku}`}
                        >
                          +
                        </button>
                      </div>

                      <div className="text-right min-w-[160px]">
                        <div className="text-xs text-gray-400">Line (excl VAT)</div>
                        <div className="text-white text-sm">R {fmt(lineExcl)}</div>
                        <div className="text-xs text-gray-400 mt-1">Line (incl VAT)</div>
                        <div className="text-ewss-cyan font-bold">R {fmt(lineIncl)}</div>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.sku)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        data-testid={`partner-cart-remove-${item.sku}`}
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Notes */}
          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Order Notes (Optional)
            </label>
            <textarea
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan"
              placeholder="Add any special instructions or notes for this order..."
              data-testid="partner-cart-notes"
            />
          </div>

          {/* Cart Summary — Subtotal / VAT / Total */}
          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-gray-300">
                <span>Subtotal (excl VAT)</span>
                <span data-testid="partner-cart-subtotal-excl">R {fmt(cartTotalExcl)}</span>
              </div>
              <div className="flex justify-between text-gray-400 text-sm">
                <span>VAT ({Math.round((vatRate ?? 0.15) * 100)}%)</span>
                <span data-testid="partner-cart-vat">R {fmt(cartVat)}</span>
              </div>
              <div className="border-t border-gray-700 pt-2 flex justify-between items-center">
                <span className="text-xl font-semibold text-white">Total (incl VAT)</span>
                <span
                  className="text-3xl font-bold text-ewss-cyan"
                  data-testid="partner-cart-total"
                >
                  R {fmt(cartTotalIncl)}
                </span>
              </div>
            </div>
            <button
              onClick={submitOrder}
              className="w-full px-6 py-4 bg-ewss-cyan text-ewss-navy font-bold rounded-lg hover:bg-ewss-cyan/90 transition-colors flex items-center justify-center gap-2"
              data-testid="partner-cart-submit-order"
            >
              <Send size={20} />
              Submit Order Request
            </button>
            <p className="text-gray-400 text-sm text-center mt-3">
              We'll review your order and send you a formal quote within 24 hours
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default PartnerCart;
